import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { Form } from 'antd';
import { ListResource } from '../../../components';
import { useColumns } from './columns';
import { ExtraFilters } from '../utils/ExtraFilters';
import { MailingModal } from '../../../components/Mailing/MailingModal';
import { useFormatter } from '../../../utils/formatter';
import { useInvoicesContext } from '../../../contexts/InvoicesContext/InvoicesContext';

/**
 * Composant de liste des factures client.
 * @component
 * @returns {JSX.Element} Composant React.
 */
export const ListCustomerInvoices = () => {
  const [form] = Form.useForm();
  const { formattedData } = useFormatter();

  const [dateFilter, setDateFilter] = useState(null);
  const [types, setTypes] = useState('');
  const [status, setStatus] = useState('');
  const [refreshData, setRefreshData] = useState(false);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [customerInvoice, setCustomerInvoice] = useState({});
  const [recipient, setRecipient] = useState([]);
  const [documentType, setDocumentType] = useState('');
  const [recordID, setRecordID] = useState('');

  const {
    enums,
    customers,
    isFieldsLoading,
    isCustomersLoading,
    loadCustomers
  } = useInvoicesContext();

  const { columns, headers } = useColumns({
    enums,
    setRefreshData,
    refreshData,
    setRecipient,
    setIsReminderModalOpen,
    setCustomerInvoice,
    setDocumentType,
    setRecordID
  });

  useEffect(() => {
    if (refreshData) setRefreshData(false);
  }, [refreshData]);

  // The reminder offers every owner and pensioner as a recipient
  useEffect(() => {
    if (isReminderModalOpen) loadCustomers();
  }, [isReminderModalOpen, loadCustomers]);

  /**
   * Builds the query string used to filter the customer invoices list.
   *
   * Applied filters:
   * - **type**: filters invoices by invoice type.
   * - **status**: filters invoices by invoice status.
   * - **dateFilter**: filters invoices based on the **due date (`due_date`)**.
   *
   * Important:
   * The date filter is applied **only on the `due_date` field** (invoice due date).
   * It does **not** filter on:
   * - `created_at` (invoice creation date)
   * - `paid_at` (invoice payment date)
   *
   * The filter covers the **entire selected day**:
   * - `due_date >= YYYY-MM-DD`
   * - `due_date <= YYYY-MM-DD 23:59:59`
   *
   * Example:
   * If the user selects `2026-03-09`, the generated query will be:
   *
   * `due_date>=2026-03-09&due_date<=2026-03-09 23:59:59`
   *
   * This ensures that all invoices with a due date within that day are returned.
   *
   * @function setExtraQuery
   * @returns {string} Query string appended to the API request to filter invoices.
   */
  const setExtraQuery = () => {
    let extraQuery = '';
    if (types) {
      extraQuery = `type=${types}`;
    }
    const separator = extraQuery ? '&' : '';
    if (status) {
      extraQuery = `${extraQuery}${separator}status=${status}`;
    }
    if (dateFilter) {
      extraQuery = `${extraQuery}${separator}due_date>=${dayjs(
        dateFilter
      ).format('YYYY-MM-DD')}&due_date<=${dayjs(dateFilter)
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss')}`;
    }

    return extraQuery;
  };

  useEffect(() => {
    setExtraQuery();
  }, [dateFilter, types, status]);

  return (
    <>
      <ListResource
        tradKey="customerinvoices"
        resourceName="customerinvoices/form"
        columns={columns}
        headers={headers}
        resourceModelName="Customerinvoice"
        populate="customer,subscription,subscription.pensioner,supervision,supervision.main_informations.owner"
        extraQuery={setExtraQuery()}
        customActionColumn
        forceRefresh={refreshData}
        withArchiveButton
        formatter={formattedData}
        withoutImportButton
        extraFilters={
          <ExtraFilters
            setDateFilter={setDateFilter}
            setTypes={setTypes}
            setStatus={setStatus}
            enums={enums}
            isFieldLoading={isFieldsLoading}
            invoiceType="customer"
          />
        }
        withoutBreadCrumb
      />
      {isReminderModalOpen && (
        <MailingModal
          isModalOpen={isReminderModalOpen}
          setIsModalOpen={setIsReminderModalOpen}
          recipients={customers}
          recipient={recipient}
          isRecipientsLoading={isCustomersLoading}
          customFormInstance={form}
          templateType="UNPAID_INVOICE_REMINDER"
          collectionData={customerInvoice}
          collection="customerinvoices"
          documentType={documentType}
          recordID={recordID}
        />
      )}
    </>
  );
};
